import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MibButtonComponent } from '@medinbox/angular';
import { catchError, EMPTY, finalize } from 'rxjs';
import { MibLogo } from '../../shared/mib-header/mib-logo';
import { CopyrightAndLegalLink } from '../../shared/copyright-and-legal-link';
import { OAuthService } from '../../core/services/oauth.service';

@Component({
  selector: 'mai-login',
  imports: [FormsModule, ReactiveFormsModule, MibButtonComponent, MibLogo, CopyrightAndLegalLink],
  templateUrl: './login.html',
})
export class Login implements OnInit {
  private readonly oAuth = inject(OAuthService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  formGroup = new FormGroup({
    username: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  submitted = signal(false);
  loading = signal(false);
  authError = signal<string | null>(null);

  get username() {
    return this.formGroup.get('username');
  }

  get password() {
    return this.formGroup.get('password');
  }

  ngOnInit(): void {
    // Normalize email to lowercase
    this.username?.valueChanges.subscribe((value) => {
      if (value && value !== value.toLowerCase()) {
        this.username?.setValue(value.toLowerCase(), { emitEvent: false });
      }
    });
  }

  onSubmit() {
    this.submitted.set(true);
    this.authError.set(null);

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.loading.set(true);

    this.oAuth
      .signIn(this.formGroup.getRawValue())
      .pipe(
        catchError((error) => {
          const name = (error && error.name) || 'UnknownAuthenticationError';
          switch (name) {
            case 'UserNotAuthorizedError':
              this.authError.set('User not authorized');
              break;
            case 'UserAlreadyAuthenticatedError':
              this.redirectAuthenticatedUser();
              break;
            case 'UserNotFoundError':
              this.authError.set('Incorrect username or password');
              break;
            default:
              this.authError.set('Unable to sign in. Please try again.');
          }
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe(() => this.redirectAuthenticatedUser());
  }

  redirectAuthenticatedUser() {
    const redirectToParam = this.activatedRoute.snapshot.queryParams['redirectTo'];
    const redirectTo = redirectToParam ? decodeURIComponent(redirectToParam) : '/';
    this.router.navigateByUrl(redirectTo);
  }
}
